import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import MedicalProviderFormDialog from "@/components/MedicalProviderFormDialog";
import MedicalProviderList from "@/components/MedicalProviderList";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Stethoscope, Plus, Search } from "lucide-react";
import { toast } from "sonner";

export default function MedicalProviders() {
  const [providers, setProviders] = useState([]);
  const [meta, setMeta] = useState({});
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pending, setPending] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (typeFilter) params.provider_type = typeFilter;
    if (statusFilter) params.treatment_status = statusFilter;
    if (pending) params.records_pending = true;
    if (search.trim()) params.search = search.trim();
    api.get("/medical-providers", { params }).then((r) => setProviders(r.data)).catch(() => {});
  }, [typeFilter, statusFilter, pending, search]);

  useEffect(() => { api.get("/medical-providers/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const confirmDelete = async () => {
    try { await api.delete(`/medical-providers/${deleteTarget.id}`); toast.success("Provider deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="medical-providers-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <Stethoscope className="w-7 h-7 text-[#002FA7]" /> Medical Providers
          </h1>
          <p className="text-sm text-gray-500 mt-1">{providers.length} provider{providers.length === 1 ? "" : "s"} across cases</p>
        </div>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-provider-button"
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> New provider
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="mp-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search provider, contact, diagnosis…"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-sm w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="mp-type-filter"
          className="text-sm border border-gray-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="">All types</option>
          {(meta.provider_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="mp-status-filter"
          className="text-sm border border-gray-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="">All statuses</option>
          {(meta.treatment_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} data-testid="mp-pending-toggle" className="accent-[#002FA7]" /> Records pending
        </label>
      </div>

      <MedicalProviderList providers={providers} showClient onEdit={(p) => { setEditing(p); setDialogOpen(true); }} onDelete={setDeleteTarget} />

      <MedicalProviderFormDialog open={dialogOpen} onOpenChange={setDialogOpen} provider={editing} meta={meta} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-provider-page-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete provider?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.provider_name}</strong> will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-provider-page-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
